import { useMemo } from "react";
import PropTypes from "prop-types";
import { Doughnut } from "react-chartjs-2";
import { Chart, ArcElement } from "chart.js";
import configs from "examples/Charts/DoughnutCharts/ProgressDoughnutChart/config";
import SoftBox from "components/SoftBox";
import { Card, Icon } from "@mui/material";
import SoftTypography from "components/SoftTypography";
import SoftBadgeDot from "components/SoftBadgeDot";

// Register necessary Chart.js elements
Chart.register(ArcElement);

function ProgressDoughnutChart({
  color = "info",
  icon,
  title,
  count = 0,
  height = "100%",
  chart,
}) {
  const { data, options } = useMemo(() => {
    // Process chart data and options here based on 'chart' prop
    return configs((chart && chart.labels) || [], chart.datasets || {});
  }, [chart]);

  const renderBadgeDots =
    chart && chart.labels
      ? chart.labels.map((label, index) => (
          <SoftBadgeDot
            key={`badge-dot-${index}`}
            variant="gradient"
            color={
              chart.datasets.backgroundColors
                ? chart.datasets.backgroundColors[index]
                : "info"
            }
            size="xs"
            badgeContent={label}
            font={{ color: "text", weight: "medium" }}
            px={0}
          />
        ))
      : null;

  return (
    <Card>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={2}
        px={2}
      >
        <SoftBox width="45%">
          <SoftBox display="flex" alignItems="center">
            <SoftBox
              width="3rem"
              height="3rem"
              display="grid"
              justifyContent="center"
              alignItems="center"
              borderRadius="md"
              shadow="md"
              color="white"
              bgColor={color}
              variant="gradient"
            >
              <Icon fontSize="default">{icon}</Icon>
            </SoftBox>
            <SoftBox ml={2} lineHeight={1}>
              <SoftTypography
                variant="button"
                fontWeight="medium"
                textTransform="capitalize"
                color="text"
              >
                {title}
              </SoftTypography>
              {count ? (
                <SoftTypography variant="h5" fontWeight="bold">
                  {count}
                </SoftTypography>
              ) : null}
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" flexDirection="column" mt={2}>
            {renderBadgeDots}
          </SoftBox>
        </SoftBox>
        <SoftBox width="55%" mb={2}>
          <Doughnut data={data} options={options} sx={{ height }} />
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

ProgressDoughnutChart.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    datasets: PropTypes.object,
  }).isRequired,
};

export default ProgressDoughnutChart;
